const RADIUS_PRESETS = {
    square: {
        surface: '0px',
        button: '0px',
        option: '0px',
        container: '0px'
    },
    mid: {
        surface: '2px',
        button: '4px',
        option: '2px',
        container: '2px'
    },
    rounded: {
        surface: '32px',
        button: '999px',
        option: '24px',
        container: '16px'
    }
};

const THEME_ATTR = 'data-fek-theme';
const SETTINGS_ATTR = 'data-fek-settings';

const isNonEmptyString = (value) =>
    typeof value === 'string' && value.trim().length > 0;

const setVarIfString = (container, variableName, value) => {
    if (isNonEmptyString(value)) {
        container.style.setProperty(variableName, value);
    }
};

const getThemePayloadValue = (container) => {
    if (!container) {
        return null;
    }

    return (
        container.getAttribute(THEME_ATTR) ||
        container.previousElementSibling?.getAttribute(THEME_ATTR)
    );
};

export const parseEmbedThemeConfig = (container) => {
    const rawThemePayload = getThemePayloadValue(container);

    if (!rawThemePayload) {
        return null;
    }

    try {
        return JSON.parse(rawThemePayload);
    } catch (error) {
        console.warn('Playbook theme: invalid data-fek-theme payload', error);
        return null;
    }
};

export const applyEmbedThemeConfig = (container, themeConfig) => {
    if (!container || !themeConfig) {
        return;
    }

    setVarIfString(container, '--backend-bg-color', themeConfig?.brand?.bg);
    setVarIfString(container, '--backend-fg-color', themeConfig?.brand?.fg);

    setVarIfString(
        container,
        '--backend-font-heading',
        themeConfig?.fonts?.heading
    );
    setVarIfString(container, '--backend-font-body', themeConfig?.fonts?.body);

    const radiusModePreset = RADIUS_PRESETS[themeConfig?.radiusMode];
    if (radiusModePreset) {
        setVarIfString(
            container,
            '--radius-surface-override',
            radiusModePreset.surface
        );
        setVarIfString(
            container,
            '--radius-button-override',
            radiusModePreset.button
        );
        setVarIfString(
            container,
            '--radius-option-override',
            radiusModePreset.option
        );
        setVarIfString(
            container,
            '--radius-container-override',
            radiusModePreset.container
        );
    }

    // Explicit radius values override radiusMode presets.
    setVarIfString(
        container,
        '--radius-surface-override',
        themeConfig?.radius?.surface
    );
    setVarIfString(
        container,
        '--radius-button-override',
        themeConfig?.radius?.button
    );
    setVarIfString(
        container,
        '--radius-option-override',
        themeConfig?.radius?.option
    );
    setVarIfString(
        container,
        '--radius-container-override',
        themeConfig?.radius?.container
    );

    if (
        themeConfig?.themeMode === 'light' ||
        themeConfig?.themeMode === 'dark'
    ) {
        container.dataset.theme = themeConfig.themeMode;
    }
};

export const applyThemeFromEmbed = (container) => {
    const themeConfig = parseEmbedThemeConfig(container);

    if (!themeConfig) {
        return;
    }

    applyEmbedThemeConfig(container, themeConfig);
};

export const parseEmbedSettingsConfig = (container) => {
    const rawSettingsPayload =
        container?.previousElementSibling?.getAttribute(SETTINGS_ATTR);

    if (!rawSettingsPayload) {
        return null;
    }

    try {
        return JSON.parse(rawSettingsPayload);
    } catch (error) {
        console.warn(
            'Playbook theme: invalid data-fek-settings payload',
            error
        );
        return null;
    }
};

export const applyLegacyEmbedColourSettings = (container, colourOverrides) => {
    if (!container?.style) {
        return;
    }

    const settings = parseEmbedSettingsConfig(container);

    if (settings) {
        setVarIfString(container, '--fek-primary', settings.primaryColour);
        setVarIfString(
            container,
            '--fek-primary-rgb',
            settings.primaryColourRgb
        );
        return;
    }

    if (colourOverrides?.hex && colourOverrides?.rgb) {
        container.style.setProperty('--fek-primary', colourOverrides.hex);
        container.style.setProperty('--fek-primary-rgb', colourOverrides.rgb);
    }
};
